import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { TableRow, TableCell, Tooltip } from '@mui/material'

import { SettingsIcon } from 'assets/icons'
import ClubsAndCoursesContainer from 'components/layout/ClubsAndCoursesContainer'
import ClubCourseSearchForm, { SEARCH_TYPE } from 'components/form/ClubCourseSearchForm'
import ClubModalForm from 'components/form/ClubModalForm'
import ErrorMessage from 'components/ui/ErrorMessage'
import InfiniteScrollTable from 'components/ui/InfiniteScrollTable'
import LoadingSpinnerGreen from 'components/ui/LoadingSpinnerGreen'
import RowMenu from 'components/ui/RowMenu'
import { ClientRoute } from 'routes/client'
import { selectClubs, saveSearchFilters } from 'store/clubs/slice'
import { selectClub, clearClub } from 'store/club/slice'
import { searchClubs } from 'store/clubs/thunks'
import { deactivateClub, reactivateClub } from 'store/club/thunks'
import { convertTimeZoneToGmtOffset } from 'utils/timeZone'
import { generateClubCourseSearchPayload } from 'utils/generateClubCourseSearchPayload'

export default function ClubSearch(): JSX.Element {
  const intl = useIntl()
  const dispatch = useDispatch()
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [currentClub, setCurrentClub] = useState<number | null>(null)
  const [noFiltersAlert, setNoFiltersAlert] = useState<string | null>(null)
  const searchLimit = 20
  const [rowMenu, setRowMenu] = useState<number | null>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const form = useForm<CourseClubSearchForm>()
  const { getValues, reset } = form
  const { clubs, status, searchFilters } = useSelector(selectClubs)
  const { saveStatus, saveError } = useSelector(selectClub)

  useEffect(() => {
    const filtersSaved = Object.keys(searchFilters).length > 0
    if (filtersSaved) {
      reset(searchFilters)
    }
  }, [searchFilters, reset])

  const submitSearchForm = (data: CourseClubSearchForm) => {
    setNoFiltersAlert(null)

    const payload = generateSearchPayload(data, 0)

    if (atLeastOneFilterGiven(payload)) {
      dispatch(searchClubs(payload))
    } else {
      setNoFiltersAlert(intl.formatMessage({ id: 'strings.selectAtLeastOneFilter' }))
    }
  }

  const generateSearchPayload = (data: CourseClubSearchForm, offset: number): ClubAndCourseSearchPayload => {
    dispatch(saveSearchFilters(data))
    return generateClubCourseSearchPayload(data, searchLimit, offset)
  }

  const atLeastOneFilterGiven = (payload: ClubAndCourseSearchPayload) => {
    const data = { ...payload.data }
    delete data.limit
    delete data.offset
    delete data.activeClubs
    return Object.keys(data).length > 0
  }

  const loadMoreResults = () => {
    if (status !== 'loading') {
      const payload = generateSearchPayload(getValues(), clubs.length)
      dispatch(searchClubs(payload))
    }
  }

  const toggleModal = () => {
    setCurrentClub(null)
    setModalOpen(!modalOpen)
  }

  const editClub = (clubId: number) => {
    setCurrentClub(clubId)
    setModalOpen(true)
    setRowMenu(null)
    setAnchorEl(null)
  }

  const columns: TableColumn[] = [
    {
      id: 'clubId',
      label: intl.formatMessage({ id: 'strings.clubId' }),
    },
    {
      id: 'club',
      label: intl.formatMessage({ id: 'strings.club' }),
    },
    {
      id: 'active',
      label: intl.formatMessage({ id: 'strings.active' }),
    },
    {
      id: 'country',
      label: intl.formatMessage({ id: 'strings.country' }),
    },
    {
      id: 'state',
      label: intl.formatMessage({ id: 'strings.state' }),
    },
    {
      id: 'timeZone',
      label: intl.formatMessage({ id: 'strings.timeZone' }),
    },
    {
      id: 'city',
      label: intl.formatMessage({ id: 'strings.city' }),
    },
    {
      id: 'officialCourses',
      label: intl.formatMessage({ id: 'strings.officialCourses' }),
    },
    {
      id: 'editClub',
      label: '',
    },
    {
      id: 'edit',
      label: '',
    },
    {
      id: 'provider',
      label: intl.formatMessage({ id: 'strings.providerMapping' }),
    },
  ]

  const isClubActive = (club: Club) => {
    const yes = intl.formatMessage({ id: 'strings.yes' })
    const no = intl.formatMessage({ id: 'strings.no' })
    return club.deactivated ? no : yes
  }

  const renderTooltipContent = (club: Club) => {
    return (
      <TooltipContent>
        <ul>
          {club.courses
            ?.filter((course: Course) => course.isActive && course.isOfficial)
            ?.map((course: Course, i: number) => <li key={i}>{course.name}</li>)}
        </ul>
      </TooltipContent>
    )
  }

  const renderTooltip = (club: Club) => {
    const isLinkedToTmOrganization = club.tmOrganizationIds && club.tmOrganizationIds.length > 0
    return (
      <Tooltip title={renderTooltipContent(club)} placement="right">
        <span>
          {club.name} {isLinkedToTmOrganization && <sup>TM</sup>}
        </span>
      </Tooltip>
    )
  }

  const handleRowMenuOpen = (event: any, id: number) => {
    setRowMenu(id)
    setAnchorEl(event.currentTarget)
  }

  const handleRowMenuClose = () => {
    setRowMenu(null)
    setAnchorEl(null)
    dispatch(clearClub())
  }

  const submitReactivation = (id: number) => {
    dispatch(reactivateClub(id))
  }

  const submitDeactivation = (id: number) => {
    dispatch(deactivateClub(id))
  }

  const getReOrDeactivateElement = (club: Club) => {
    if (club.deactivated) {
      return <div onClick={() => submitReactivation(club.id)}>{intl.formatMessage({ id: 'strings.reactivate' })}</div>
    }
    return <div onClick={() => submitDeactivation(club.id)}>{intl.formatMessage({ id: 'strings.deactivate' })}</div>
  }

  const getTimeZone = (timeZone: TimeZone | undefined): string => {
    return convertTimeZoneToGmtOffset(timeZone).replace('(', '').replace(')', '')
  }

  return (
    <ClubsAndCoursesContainer>
      <>
        <SearchForm>
          <PageTitle>{intl.formatMessage({ id: 'strings.searchForClubs' })}</PageTitle>
          <ClubCourseSearchForm
            form={form}
            handleSearch={submitSearchForm}
            type={SEARCH_TYPE.Club}
            toggleModal={toggleModal}
            searchStatus={status}
          />
          <ErrorContainer>{noFiltersAlert && <ErrorMessage>{noFiltersAlert}</ErrorMessage>}</ErrorContainer>
        </SearchForm>
        {modalOpen && <ClubModalForm open={modalOpen} toggle={toggleModal} clubId={currentClub} />}

        <SearchResults>
          <InfiniteScrollTable
            columns={columns}
            rowCount={clubs.length}
            hasMoreRows={clubs.length >= searchLimit && clubs.length % searchLimit === 0}
            loadMore={loadMoreResults}
            noMoreRowsText={clubs.length > 0 ? intl.formatMessage({ id: 'strings.noMoreResults' }) : ''}
            status={status}
          >
            {clubs.map((club, i) => (
              <TableRow hover key={i}>
                <TableCell>{club.id}</TableCell>
                <TableCell>
                  <ClubName to={ClientRoute.ADMIN_CLUB_COURSES(club.id)}>{renderTooltip(club)}</ClubName>
                </TableCell>
                <TableCell>{isClubActive(club)}</TableCell>
                <TableCell>{club.country?.name}</TableCell>
                <TableCell>{club.state}</TableCell>
                <TableCell>{getTimeZone(club.timeZone)}</TableCell>
                <TableCell>{club.city}</TableCell>
                <TableCell>{`${club.officialCourses}/${club.totalCourses}`}</TableCell>
                <TableCell>
                  <EditClub onClick={() => editClub(club.id)}>
                    {intl.formatMessage({ id: 'strings.editClub' })}
                  </EditClub>
                </TableCell>
                <TableCell>
                  <ActionMenuIcon onClick={(e) => handleRowMenuOpen(e, club.id)} />
                  <RowMenu open={club.id === rowMenu} anchorEl={anchorEl} handleClose={handleRowMenuClose}>
                    {saveStatus === 'loading' ? (
                      <LoadingSpinnerGreen />
                    ) : (
                      <>
                        {saveStatus === 'failed' && saveError && <ErrorMessage>{saveError}</ErrorMessage>}
                        {getReOrDeactivateElement(club)}
                        <div className="disabled">{intl.formatMessage({ id: 'strings.remove' })}</div>
                      </>
                    )}
                  </RowMenu>
                </TableCell>
                <TableCell>{club.provider?.abbreviation || '-'}</TableCell>
              </TableRow>
            ))}
          </InfiniteScrollTable>
        </SearchResults>
      </>
    </ClubsAndCoursesContainer>
  )
}

const PageTitle = styled.h1`
  font-style: italic;
  font-weight: 800;
  font-size: 20px;
  line-height: 26px;
`

const SearchForm = styled.div`
  background-color: ${(props) => props.theme.customPalette.light};
  padding: 20px 40px;
`

const SearchResults = styled(SearchForm)``

const ErrorContainer = styled.div`
  margin-top: 30px;
`

const ActionMenuIcon = styled(SettingsIcon)`
  width: 20px;
  height: 20px;
  cursor: pointer;
`

const TooltipContent = styled.div`
  border: none;
  padding: 10px 5px;
  & li {
    font-size: 1.2em;
    margin: 5px 0;
  }
`

const ClubName = styled(Link)`
  color: ${(props) => props.theme.palette.primary.main};

  & sup {
    color: #404040;
    font-size: 10px;
    line-height: 12px;
    font-weight: 700;
    vertical-align: top;
  }
`

const EditClub = styled.span`
  color: ${(props) => props.theme.palette.primary.main};
  cursor: pointer;
`
